import React, {useContext, useMemo, useState} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ToggleSwitch from "../components/ToggleSwitch/ToggleSwitch";
import StyleContext from "../contexts/StyleContext";
import {useLocalStorage} from "../hooks/useLocalStorage";
import {
  createBlogPost,
  deleteBlogPost,
  fetchAdminBlogs,
  updateBlogPost
} from "../utils/blogApi";
import {
  createEmptyBlogPost,
  formatBlogDate,
  normalizeTags,
  slugify
} from "../utils/blogs";
import "../containers/blogs/Blog.scss";
import "./BlogAdminPage.scss";

const ADMIN_USERNAME_STORAGE_KEY = "portfolio_blog_admin_username";

function toEditorState(post) {
  return {
    id: post.id,
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt,
    content: post.content,
    coverImage: post.coverImage || "",
    tagsText: Array.isArray(post.tags) ? post.tags.join(", ") : "",
    status: post.status,
    createdAt: post.createdAt,
    updatedAt: post.updatedAt
  };
}

function createBlankEditorState() {
  const emptyPost = createEmptyBlogPost();
  return {
    id: emptyPost.id,
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    coverImage: "",
    tagsText: "",
    status: "draft",
    createdAt: emptyPost.createdAt,
    updatedAt: emptyPost.updatedAt
  };
}

export default function BlogAdminPage() {
  const {isDark} = useContext(StyleContext);
  const [adminUsername, setAdminUsername] = useLocalStorage(
    ADMIN_USERNAME_STORAGE_KEY,
    "admin"
  );
  const [adminPassword, setAdminPassword] = useState("");
  const [posts, setPosts] = useState([]);
  const [editorState, setEditorState] = useState(createBlankEditorState());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState("edit");
  const [status, setStatus] = useState({
    type: "idle",
    message: "Use the admin username and password from your server env to load drafts."
  });
  const [hasAuthenticated, setHasAuthenticated] = useState(false);

  const selectedPostId = editorState.id;
  const selectedPost = useMemo(
    () => posts.find(post => post.id === selectedPostId),
    [posts, selectedPostId]
  );

  const adminCredentials = {
    username: adminUsername.trim(),
    password: adminPassword
  };

  const isEditorLocked = !hasAuthenticated;

  async function loadPosts(event) {
    if (event) {
      event.preventDefault();
    }

    if (!adminCredentials.username || !adminCredentials.password) {
      setStatus({
        type: "error",
        message: "Enter the admin username and password before connecting."
      });
      return;
    }

    setIsLoading(true);
    setStatus({type: "idle", message: "Connecting to MongoDB writer..."});
    try {
      const response = await fetchAdminBlogs(adminCredentials);

      setPosts(response.posts || []);
      if (response.posts && response.posts.length > 0) {
        setEditorState(toEditorState(response.posts[0]));
      } else {
        setEditorState(createBlankEditorState());
      }
      setHasAuthenticated(true);
      setStatus({type: "success", message: "Writer connected to MongoDB."});
    } catch (error) {
      setHasAuthenticated(false);
      setPosts([]);
      setEditorState(createBlankEditorState());
      setStatus({type: "error", message: error.message});
    } finally {
      setIsLoading(false);
    }
  }

  function updateField(field, value) {
    setEditorState(previousState => Object.assign({}, previousState, {[field]: value}));
  }

  function updateCredentials(field, value) {
    if (field === "username") {
      setAdminUsername(value);
    } else {
      setAdminPassword(value);
    }

    if (hasAuthenticated) {
      setHasAuthenticated(false);
      setStatus({
        type: "idle",
        message: "Credentials changed. Reconnect before editing or publishing."
      });
    }
  }

  function resetEditor() {
    setEditorState(createBlankEditorState());
    setStatus({type: "idle", message: hasAuthenticated ? "Ready." : status.message});
  }

  function resetAccess() {
    setAdminUsername("admin");
    setAdminPassword("");
    setHasAuthenticated(false);
    setPosts([]);
    setEditorState(createBlankEditorState());
    setStatus({
      type: "idle",
      message: "Use the admin username and password from your server env to load drafts."
    });
  }

  async function savePost(nextStatus) {
    if (isEditorLocked) {
      setStatus({type: "error", message: "Connect with admin credentials before saving."});
      return;
    }

    setIsSaving(true);
    setStatus({type: "idle", message: ""});
    try {
      const payload = {
        title: editorState.title,
        slug: editorState.slug || slugify(editorState.title),
        excerpt: editorState.excerpt,
        content: editorState.content,
        coverImage: editorState.coverImage,
        tags: normalizeTags(editorState.tagsText),
        status: nextStatus
      };

      const response = selectedPost
        ? await updateBlogPost(selectedPost.id, payload, adminCredentials)
        : await createBlogPost(payload, adminCredentials);

      const savedPost = response.post;
      const nextPosts = posts
        .filter(post => post.id !== savedPost.id)
        .concat(savedPost)
        .sort((firstPost, secondPost) => {
          return new Date(secondPost.updatedAt).getTime() - new Date(firstPost.updatedAt).getTime();
        });
      setPosts(nextPosts);
      setEditorState(toEditorState(savedPost));
      setStatus({
        type: "success",
        message:
          nextStatus === "published"
            ? "Post published to MongoDB and the public site."
            : "Draft saved to MongoDB."
      });
    } catch (error) {
      setStatus({type: "error", message: error.message});
    } finally {
      setIsSaving(false);
    }
  }

  async function removePost() {
    if (!selectedPost) {
      return;
    }

    if (isEditorLocked) {
      setStatus({type: "error", message: "Connect with admin credentials before deleting."});
      return;
    }

    if (!window.confirm(`Delete "${selectedPost.title}"?`)) {
      return;
    }

    setIsSaving(true);
    try {
      await deleteBlogPost(selectedPost.id, adminCredentials);
      const nextPosts = posts.filter(post => post.id !== selectedPost.id);
      setPosts(nextPosts);
      setEditorState(nextPosts.length ? toEditorState(nextPosts[0]) : createBlankEditorState());
      setStatus({type: "success", message: "Post deleted."});
    } catch (error) {
      setStatus({type: "error", message: error.message});
    } finally {
      setIsSaving(false);
    }
  }

  function importMarkdown(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = loadEvent => {
      const content = String(loadEvent.target && loadEvent.target.result ? loadEvent.target.result : "");
      const headingMatch = content.match(/^#\s+(.+)$/m);
      const inferredTitle = headingMatch ? headingMatch[1].trim() : file.name.replace(/\.md$/i, "");

      setEditorState(previousState =>
        Object.assign({}, previousState, {
          title: previousState.title || inferredTitle,
          slug: previousState.slug || slugify(inferredTitle),
          content
        })
      );
      setViewMode("preview");
      setStatus({type: "success", message: `Imported ${file.name}.`});
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  return (
    <div className={isDark ? "admin-page admin-page-dark" : "admin-page"}>
      <div className="admin-page-shell">
        <header className="admin-page-header">
          <div>
            <div className="blog-kicker">Admin route</div>
            <h1>Blog Editor</h1>
            <p>
              Markdown-first writing workspace backed by MongoDB. Drafts stay hidden
              until you publish them.
            </p>
          </div>

          <div className="admin-page-actions">
            <a className="admin-page-link" href="/">
              Back to portfolio
            </a>
            <ToggleSwitch />
          </div>
        </header>

        <section className={isDark ? "admin-auth-card admin-auth-card-dark" : "admin-auth-card"}>
          <div>
            <strong>Admin access</strong>
            <p>
              Use `BLOG_ADMIN_USERNAME` and `BLOG_ADMIN_PASSWORD` from your server
              env (`.env` or `.env.local`).
            </p>
          </div>

          <form className="admin-auth-controls" onSubmit={loadPosts}>
            <input
              className="blog-input"
              type="text"
              value={adminUsername}
              onChange={event => updateCredentials("username", event.target.value)}
              placeholder="Admin username"
              autoComplete="username"
            />
            <input
              className="blog-input"
              type="password"
              value={adminPassword}
              onChange={event => updateCredentials("password", event.target.value)}
              placeholder="Admin password"
              autoComplete="current-password"
            />
            <button className="blog-editor-button" disabled={isLoading} type="submit">
              {isLoading ? "Connecting" : "Connect"}
            </button>
            <button
              className="blog-editor-button blog-editor-button-ghost"
              onClick={resetAccess}
              type="button"
            >
              Reset
            </button>
          </form>
        </section>

        <section className="admin-layout">
          <aside className={isDark ? "admin-sidebar admin-sidebar-dark" : "admin-sidebar"}>
            <div className="admin-sidebar-header">
              <div className="blog-admin-panel-title">Your posts</div>
              <button className="blog-editor-button blog-editor-button-ghost" disabled={isEditorLocked} onClick={resetEditor} type="button">
                New note
              </button>
            </div>

            <div className="admin-sidebar-list">
              {isLoading ? <div className="admin-sidebar-empty">Loading posts...</div> : null}
              {!isLoading && isEditorLocked ? (
                <div className="admin-sidebar-empty">Connect with admin credentials to load drafts.</div>
              ) : null}
              {!isLoading && !isEditorLocked && posts.length === 0 ? (
                <div className="admin-sidebar-empty">No custom posts yet.</div>
              ) : null}
              {posts.map(post => (
                <button
                  key={post.id}
                  className={selectedPostId === post.id ? "admin-post admin-post-active" : "admin-post"}
                  onClick={() => setEditorState(toEditorState(post))}
                  type="button"
                >
                  <span className="admin-post-status">
                    {post.status === "published" ? "Published" : "Draft"}
                  </span>
                  <strong>{post.title}</strong>
                  <span>{formatBlogDate(post.updatedAt)}</span>
                </button>
              ))}
            </div>
          </aside>

          <section className={isDark ? "admin-editor admin-editor-dark" : "admin-editor"}>
            <div className="admin-editor-toolbar">
              <div className="blog-admin-panel-title">Editor</div>
              <div className="admin-editor-toolbar-actions">
                <label className="admin-import-button">
                  Import Markdown
                  <input type="file" accept=".md,.markdown,text/markdown,text/plain" onChange={importMarkdown} />
                </label>
                <button
                  className={viewMode === "edit" ? "admin-view-toggle admin-view-toggle-active" : "admin-view-toggle"}
                  onClick={() => setViewMode("edit")}
                  type="button"
                >
                  Edit
                </button>
                <button
                  className={viewMode === "preview" ? "admin-view-toggle admin-view-toggle-active" : "admin-view-toggle"}
                  onClick={() => setViewMode("preview")}
                  type="button"
                >
                  Preview
                </button>
              </div>
            </div>

            <div className="admin-editor-grid">
              <div className="admin-editor-form">
                <label className="blog-field-label" htmlFor="admin-title">
                  Title
                </label>
                <input
                  className="blog-input"
                  id="admin-title"
                  type="text"
                  disabled={isEditorLocked}
                  value={editorState.title}
                  onChange={event => updateField("title", event.target.value)}
                  placeholder="How this system scales in production"
                />

                <label className="blog-field-label" htmlFor="admin-slug">
                  Slug
                </label>
                <input
                  className="blog-input"
                  id="admin-slug"
                  type="text"
                  disabled={isEditorLocked}
                  value={editorState.slug}
                  onChange={event => updateField("slug", event.target.value)}
                  placeholder="leave blank to auto-generate"
                />

                <label className="blog-field-label" htmlFor="admin-cover-image">
                  Cover image URL
                </label>
                <input
                  className="blog-input"
                  id="admin-cover-image"
                  type="text"
                  disabled={isEditorLocked}
                  value={editorState.coverImage}
                  onChange={event => updateField("coverImage", event.target.value)}
                  placeholder="https://..."
                />

                <label className="blog-field-label" htmlFor="admin-tags">
                  Tags
                </label>
                <input
                  className="blog-input"
                  id="admin-tags"
                  type="text"
                  disabled={isEditorLocked}
                  value={editorState.tagsText}
                  onChange={event => updateField("tagsText", event.target.value)}
                  placeholder="backend, mongodb, architecture"
                />

                <label className="blog-field-label" htmlFor="admin-excerpt">
                  Excerpt
                </label>
                <textarea
                  className="blog-textarea"
                  id="admin-excerpt"
                  rows="4"
                  disabled={isEditorLocked}
                  value={editorState.excerpt}
                  onChange={event => updateField("excerpt", event.target.value)}
                  placeholder="Short summary shown on the public card"
                />

                <label className="blog-field-label" htmlFor="admin-content">
                  Content
                </label>
                <textarea
                  className="blog-textarea blog-textarea-large"
                  id="admin-content"
                  rows="16"
                  disabled={isEditorLocked}
                  value={editorState.content}
                  onChange={event => updateField("content", event.target.value)}
                  placeholder={"# Post title\n\nWrite Markdown or plain text here.\n\n- bullets\n- tables\n- code blocks"}
                />
              </div>

              <div className={isDark ? "admin-preview admin-preview-dark" : "admin-preview"}>
                <div className="blog-admin-panel-title">Preview</div>
                {editorState.coverImage ? (
                  <div className="admin-preview-cover">
                    <img src={editorState.coverImage} alt={editorState.title || "Preview cover"} />
                  </div>
                ) : null}
                <h2>{editorState.title || "Untitled draft"}</h2>
                <p className="admin-preview-excerpt">
                  {editorState.excerpt || "Add an excerpt to shape the card summary."}
                </p>
                <div className="admin-preview-tags">
                  {normalizeTags(editorState.tagsText).map(tag => (
                    <span className="blog-tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className={viewMode === "preview" ? "admin-markdown admin-markdown-visible" : "admin-markdown"}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {editorState.content || "Write Markdown or plain text to preview it here."}
                  </ReactMarkdown>
                </div>
              </div>
            </div>

            <div className="admin-editor-footer">
              <div className={status.type === "error" ? "admin-status admin-status-error" : "admin-status"}>
                {status.message || "Ready."}
              </div>
              <div className="admin-editor-actions">
                {selectedPost ? (
                  <button className="blog-editor-button blog-editor-button-danger" disabled={isSaving || isEditorLocked} onClick={removePost} type="button">
                    Delete
                  </button>
                ) : null}
                <button className="blog-editor-button blog-editor-button-ghost" disabled={isSaving || isEditorLocked} onClick={() => savePost("draft")} type="button">
                  Save Draft
                </button>
                <button className="blog-editor-button" disabled={isSaving || isEditorLocked} onClick={() => savePost("published")} type="button">
                  Publish
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}
